import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface FormsState {
  formDataByScreen: Record<string, Record<string, any>>;
  loadingByScreen: Record<string, boolean>;
  savingByScreen: Record<string, boolean>;
  /** True while ExpressionV2 fields are still settling after hydrate / bulk populate */
  expressionCalculatingByScreen: Record<string, boolean>;
  formInstanceCounter: number;
  editModeByScreen: Record<
    string,
    { isEditMode: boolean; recordId?: string | number; isViewOnly?: boolean }
  >;
}

const initialState: FormsState = {
  formDataByScreen: {},
  loadingByScreen: {},
  savingByScreen: {},
  expressionCalculatingByScreen: {},
  formInstanceCounter: 0,
  editModeByScreen: {},
};

const formsSlice = createSlice({
  name: "forms",
  initialState,
  reducers: {
    setScreenFormData(
      state,
      action: PayloadAction<{ screenId: string; data: Record<string, any> }>
    ) {
      state.formDataByScreen[action.payload.screenId] = action.payload.data;
    },
    updateScreenFormField(
      state,
      action: PayloadAction<{
        screenId: string;
        name: string;
        value: any;
      }>
    ) {
      const current =
        state.formDataByScreen[action.payload.screenId] || {};
      state.formDataByScreen[action.payload.screenId] = {
        ...current,
        [action.payload.name]: action.payload.value,
      };
    },
    resetScreenFormData(state, action: PayloadAction<string>) {
      delete state.formDataByScreen[action.payload];
      delete state.editModeByScreen[action.payload];
      delete state.expressionCalculatingByScreen[action.payload];
    },
    setScreenLoading(
      state,
      action: PayloadAction<{ screenId: string; isLoading: boolean }>
    ) {
      state.loadingByScreen[action.payload.screenId] =
        action.payload.isLoading;
    },
    setScreenSaving(
      state,
      action: PayloadAction<{ screenId: string; isSaving: boolean }>
    ) {
      state.savingByScreen[action.payload.screenId] =
        action.payload.isSaving;
    },
    setScreenExpressionCalculating(
      state,
      action: PayloadAction<{ screenId: string; isCalculating: boolean }>
    ) {
      state.expressionCalculatingByScreen[action.payload.screenId] =
        action.payload.isCalculating;
    },
    incrementFormInstanceCounter(state) {
      state.formInstanceCounter += 1;
    },
    setFormInstanceCounter(state, action: PayloadAction<number>) {
      state.formInstanceCounter = action.payload;
    },
    setScreenEditMode(
      state,
      action: PayloadAction<{
        screenId: string;
        isEditMode: boolean;
        recordId?: string | number;
        isViewOnly?: boolean;
      }>
    ) {
      state.editModeByScreen[action.payload.screenId] = {
        isEditMode: action.payload.isEditMode,
        recordId: action.payload.recordId,
        isViewOnly: action.payload.isViewOnly ?? false,
      };
    },
    clearScreenEditMode(state, action: PayloadAction<string>) {
      delete state.editModeByScreen[action.payload];
    },
  },
});

export const {
  setScreenFormData,
  updateScreenFormField,
  resetScreenFormData,
  setScreenLoading,
  setScreenSaving,
  setScreenExpressionCalculating,
  incrementFormInstanceCounter,
  setFormInstanceCounter,
  setScreenEditMode,
  clearScreenEditMode,
} = formsSlice.actions;

export default formsSlice.reducer;

